import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Badge,
  Stack,
  Alert,
  Progress,
  RingProgress,
  SimpleGrid,
  Select,
  ActionIcon,
  Tooltip,
  Switch,
  ThemeIcon,
} from '@mantine/core';
import {
  IconActivity,
  IconClock,
  IconCheck,
  IconAlertTriangle,
  IconRefresh,
  IconRobot,
  IconTrendingUp,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { getProcessingProgress, getDailyProcessingProgress } from '../../services/parentService';
import type { DailyProcessingProgress } from '../../services/parentService';
import { SmartLoader } from '../../components/common/LoadingStates';

const ChildAIProgressPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const queryClient = useQueryClient();

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('completion_rate');

  // Fetch today's processing progress
  const {
    data: todayProgress,
    isLoading: todayLoading,
    error: todayError,
  } = useQuery({
    queryKey: ['processing-progress', childId],
    queryFn: () => getProcessingProgress(childId!),
    enabled: !!childId,
    refetchInterval: autoRefresh ? 60000 : false, // Refresh every minute if auto-refresh is on
  });

  // Fetch daily processing progress (past week)
  const {
    data: weeklyProgress,
    isLoading: weeklyLoading,
    error: weeklyError,
  } = useQuery({
    queryKey: ['daily-processing-progress', childId],
    queryFn: () => getDailyProcessingProgress(childId!),
    enabled: !!childId,
    refetchInterval: autoRefresh ? 300000 : false, // Refresh every 5 minutes
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['processing-progress', childId] });
    queryClient.invalidateQueries({ queryKey: ['daily-processing-progress', childId] });
  };

  if (todayLoading || weeklyLoading) {
    return <SmartLoader isLoading={true}>Loading AI processing progress...</SmartLoader>;
  }

  if (todayError || weeklyError) {
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Error Loading Progress Data"
          color="red"
        >
          Unable to load AI processing progress. Please try again later.
        </Alert>
      </Container>
    );
  }

  // Prepare chart data for hourly breakdown
  const hourlyData = todayProgress?.hourly_breakdown ?
    Object.entries(todayProgress.hourly_breakdown).map(([hour, count]) => ({
      hour: `${hour}:00`,
      activities: count,
    })) : [];

  // Prepare weekly chart data
  const weeklyData = weeklyProgress?.data?.map((day: DailyProcessingProgress) => ({
    date: new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    completion_rate: day.completion_rate,
    total_activities: day.total_activities,
    verified_activities: day.verified_activities,
  })) || [];

  // Activity types data for pie chart
  const activityTypesData = todayProgress?.activity_types ?
    Object.entries(todayProgress.activity_types).map(([type, count]) => ({
      name: type.replace('_', ' ').toUpperCase(),
      value: count,
      color: getActivityTypeColor(type),
    })) : [];

  function getActivityTypeColor(type: string): string {
    const colors: Record<string, string> = {
      'playing': '#4CAF50',
      'learning': '#2196F3',
      'eating': '#FF9800',
      'sleeping': '#9C27B0',
      'social': '#00BCD4',
      'physical': '#F44336',
      'creative': '#E91E63',
      'reading': '#3F51B5',
    };
    return colors[type] || '#757575';
  }

  const completionRate = todayProgress?.processing_completion_rate || 0;
  const totalActivities = todayProgress?.total_activities_detected || 0;
  const verifiedActivities = todayProgress?.verified_activities || 0;
  const pendingActivities = todayProgress?.pending_verification || 0;

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>AI Video Processing Progress</Title>
            <Text c="dimmed">
              {todayProgress?.child_name} - {todayProgress?.date}
            </Text>
          </div>
          <Group>
            <Tooltip label="Auto-refresh progress data">
              <Switch
                label="Auto-refresh"
                checked={autoRefresh}
                onChange={(event) => setAutoRefresh(event.currentTarget.checked)}
              />
            </Tooltip>
            <ActionIcon variant="light" onClick={handleRefresh}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Today's Overview Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <Card withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  Total Activities
                </Text>
                <Text fw={700} size="xl">
                  {totalActivities}
                </Text>
              </div>
              <ThemeIcon color="blue" variant="light" size="xl">
                <IconActivity size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  Verified
                </Text>
                <Text fw={700} size="xl">
                  {verifiedActivities}
                </Text>
              </div>
              <ThemeIcon color="green" variant="light" size="xl">
                <IconCheck size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  Pending
                </Text>
                <Text fw={700} size="xl">
                  {pendingActivities}
                </Text>
              </div>
              <ThemeIcon color="orange" variant="light" size="xl">
                <IconClock size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder>
            <Group justify="space-between">
              <div>
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  Completion Rate
                </Text>
                <Text fw={700} size="xl">
                  {completionRate.toFixed(1)}%
                </Text>
              </div>
              <ThemeIcon color="teal" variant="light" size="xl">
                <IconTrendingUp size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Processing Progress Ring */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={500}>Today's Processing Status</Text>
                  <Badge color={completionRate >= 90 ? 'green' : completionRate >= 70 ? 'yellow' : 'red'}>
                    {completionRate >= 90 ? 'Excellent' : completionRate >= 70 ? 'Good' : 'Needs Attention'}
                  </Badge>
                </Group>

                <Group justify="center">
                  <RingProgress
                    size={200}
                    thickness={20}
                    sections={[
                      { value: completionRate, color: completionRate >= 90 ? 'green' : completionRate >= 70 ? 'yellow' : 'red' },
                    ]}
                    label={
                      <div style={{ textAlign: 'center' }}>
                        <Text fw={700} size="xl">
                          {completionRate.toFixed(1)}%
                        </Text>
                        <Text size="sm" c="dimmed">
                          Complete
                        </Text>
                      </div>
                    }
                  />
                </Group>

                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm">Verified Activities</Text>
                    <Progress value={(verifiedActivities / totalActivities) * 100} size="sm" style={{ flex: 1, marginLeft: 16 }} />
                    <Text size="sm" fw={500}>{verifiedActivities}/{totalActivities}</Text>
                  </Group>

                  {todayProgress?.last_activity_time && (
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Last Activity:</Text>
                      <Text size="sm">
                        {new Date(todayProgress.last_activity_time).toLocaleTimeString()}
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder>
              <Stack gap="md">
                <Text fw={500}>Activity Types Today</Text>

                {activityTypesData.length > 0 ? (
                  <Stack gap="xs">
                    {activityTypesData.map((activity, index) => (
                      <Group key={index} justify="space-between">
                        <Group gap="xs">
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: activity.color,
                            }}
                          />
                          <Text size="sm">{activity.name}</Text>
                        </Group>
                        <Badge variant="light" size="sm">
                          {activity.value}
                        </Badge>
                      </Group>
                    ))}
                  </Stack>
                ) : (
                  <Text c="dimmed" ta="center" py="xl">
                    No activities detected today
                  </Text>
                )}
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Hourly Activity Chart */}
        <Card withBorder>
          <Stack gap="md">
            <Text fw={500}>Hourly Activity Distribution</Text>

            {hourlyData.length > 0 ? (
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="activities" fill="#4CAF50" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No hourly data available
              </Text>
            )}
          </Stack>
        </Card>

        {/* Weekly Progress Chart */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>Weekly Progress Trend</Text>
              <Select
                value={selectedMetric}
                onChange={(value) => setSelectedMetric(value || 'completion_rate')}
                data={[
                  { value: 'completion_rate', label: 'Completion Rate' },
                  { value: 'total_activities', label: 'Total Activities' },
                  { value: 'verified_activities', label: 'Verified Activities' },
                ]}
                size="sm"
                w={200}
              />
            </Group>

            {weeklyData.length > 0 ? (
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Area
                      type="monotone"
                      dataKey={selectedMetric}
                      stroke="#2196F3"
                      fill="#2196F3"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No weekly data available
              </Text>
            )}
          </Stack>
        </Card>

        {/* AI Processing Info */}
        <Alert
          icon={<IconRobot size={16} />}
          title="About AI Video Processing"
          color="blue"
        >
          <Text size="sm">
            Our AI system continuously analyzes video footage to detect and classify your child's activities.
            The processing completion rate indicates how many detected activities have been verified by our teachers.
            Higher completion rates mean more accurate and reliable activity tracking.
          </Text>
        </Alert>
      </Stack>
    </Container>
  );
};

export default ChildAIProgressPage;
